import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { HandleError } from "shared/utils";
import { NotistackServices } from "shared/services";
import { McpLibraryServices, PublicMcpDTO, CreateMcpPayload, UpdateMcpPayload, PricingModelDTO, ToolENV } from "shared/services/McpLibraryServices";

import styles from "./styles.module.scss";

// --- Types & helpers (reused from McpFormDialog) ---

interface McpFormState {
	slug: string;
	name: string;
	category: string;
	description: string;
	systemPrompt: string;
	iconUrl: string;
	mcpServerConfig: string;
	toolEnvFields: ToolENV[];
	pricingJunior: string;
	pricingPleno: string;
	pricingSenior: string;
}

const emptyForm: McpFormState = {
	slug: "",
	name: "",
	category: "general",
	description: "",
	systemPrompt: "",
	iconUrl: "",
	mcpServerConfig: "{}",
	toolEnvFields: [],
	pricingJunior: "",
	pricingPleno: "",
	pricingSenior: "",
};

function mcpToForm(mcp: PublicMcpDTO): McpFormState {
	return {
		slug: mcp.slug,
		name: mcp.name,
		category: mcp.category,
		description: mcp.description ?? "",
		systemPrompt: "",
		iconUrl: mcp.iconUrl ?? "",
		mcpServerConfig: JSON.stringify(mcp.mcpServerConfig ?? {}, null, 2),
		toolEnvFields: (mcp.toolEnvFields ?? []).map((field) => ({
			...field,
			scopes: field.scopes ?? field.oauthScopes ?? [],
		})),
		pricingJunior: mcp.pricingModel?.junior?.toString() ?? "",
		pricingPleno: mcp.pricingModel?.pleno?.toString() ?? "",
		pricingSenior: mcp.pricingModel?.senior?.toString() ?? "",
	};
}

const BASE_ENV_TYPES: Array<Exclude<ToolENV["type"], `OAuth2:${string}`>> = ["string", "number", "boolean", "json"];
const MCP_SLUG_REGEX = /^mcp-(([a-z0-9]+-?){1,})$/;

function getToolEnvMode(type: ToolENV["type"]): Exclude<ToolENV["type"], `OAuth2:${string}`> | "oauth2" {
	if (type.startsWith("OAuth2:")) return "oauth2";
	if (type === "string" || type === "number" || type === "boolean" || type === "json") return type;
	return "string";
}

function getOAuthProvider(type: ToolENV["type"]): string {
	return type.startsWith("OAuth2:") ? type.slice("OAuth2:".length) : "";
}

function getOAuthScopes(field: ToolENV): string[] {
	if (Array.isArray(field.scopes)) {
		return field.scopes.map((scope) => String(scope ?? "").trim()).filter(Boolean);
	}

	if (Array.isArray(field.oauthScopes)) {
		return field.oauthScopes.map((scope) => String(scope ?? "").trim()).filter(Boolean);
	}

	return [];
}

function createEmptyToolEnv(): ToolENV {
	return {
		envName: "",
		type: "string",
		label: "",
		description: "",
		required: false,
	};
}

function validateToolEnvFields(toolEnvFields: ToolENV[]): string | null {
	const envNameSet = new Set<string>();

	for (const [index, field] of toolEnvFields.entries()) {
		const envName = field.envName.trim();
		const label = field.label.trim();

		if (!envName) {
			return `Campo ENV #${index + 1}: envName é obrigatório.`;
		}

		if (!/^[A-Z][A-Z0-9_]*$/i.test(envName)) {
			return `Campo ENV #${index + 1}: envName deve conter apenas letras, números e underscore.`;
		}

		if (envNameSet.has(envName.toLowerCase())) {
			return `Campo ENV #${index + 1}: envName duplicado (${envName}).`;
		}
		envNameSet.add(envName.toLowerCase());

		if (!label) {
			return `Campo ENV #${index + 1}: label é obrigatório.`;
		}

		const validType = BASE_ENV_TYPES.includes(field.type as any) || /^OAuth2:.+$/i.test(field.type);
		if (!validType) {
			return `Campo ENV #${index + 1}: tipo inválido.`;
		}

		if (field.type.startsWith("OAuth2:")) {
			const scopes = getOAuthScopes(field);
			if (scopes.length === 0) {
				return `Campo ENV #${index + 1}: informe ao menos um escopo OAuth2.`;
			}
		}
	}

	return null;
}

function splitList(value: string): string[] {
	return value
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean);
}

function buildPricingModel(form: McpFormState): PricingModelDTO | undefined {
	const pricingModel: PricingModelDTO = {};
	if (form.pricingJunior) pricingModel.junior = parseFloat(form.pricingJunior);
	if (form.pricingPleno) pricingModel.pleno = parseFloat(form.pricingPleno);
	if (form.pricingSenior) pricingModel.senior = parseFloat(form.pricingSenior);
	return Object.keys(pricingModel).length > 0 ? pricingModel : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
	if (!isRecord(value)) return false;
	return Object.values(value).every((item) => typeof item === "string");
}

function isStringArray(value: unknown): value is string[] {
	return Array.isArray(value) && value.every((item) => typeof item === "string");
}

interface McpConfig {
	mcpServers: {
		[serverName: string]: Record<string, any>;
	};
}

function validateServerConfig(config: unknown, serverName: string): string | null {
	if (!isRecord(config)) {
		return `Servidor "${serverName}" deve ser um objeto.`;
	}

	const hasCommand = "command" in config;
	const hasUrl = "url" in config;

	if (hasCommand && hasUrl) {
		return `Servidor "${serverName}" não pode ter "command" e "url" ao mesmo tempo.`;
	}

	if (!hasCommand && !hasUrl) {
		return `Servidor "${serverName}" precisa ter "command" (stdio) ou "url" (sse).`;
	}

	if (hasCommand) {
		if (typeof config.command !== "string" || !config.command.trim()) {
			return `Servidor "${serverName}": "command" é obrigatório e deve ser string.`;
		}

		if ("args" in config && !isStringArray(config.args)) {
			return `Servidor "${serverName}": "args" deve ser string[].`;
		}

		if ("env" in config && !isStringRecord(config.env)) {
			return `Servidor "${serverName}": "env" deve ser Record<string, string>.`;
		}

		if ("cwd" in config && typeof config.cwd !== "string") {
			return `Servidor "${serverName}": "cwd" deve ser string.`;
		}
	}

	if (hasUrl) {
		if (typeof config.url !== "string" || !config.url.trim()) {
			return `Servidor "${serverName}": "url" é obrigatório e deve ser string.`;
		}

		if ("headers" in config && !isStringRecord(config.headers)) {
			return `Servidor "${serverName}": "headers" deve ser Record<string, string>.`;
		}

		if ("authProvider" in config) {
			const authProvider = config.authProvider;

			if (typeof authProvider !== "string") {
				if (!isRecord(authProvider)) {
					return `Servidor "${serverName}": "authProvider" deve ser string ou objeto.`;
				}

				if (!["bearer", "basic", "custom"].includes(String(authProvider.type))) {
					return `Servidor "${serverName}": "authProvider.type" deve ser bearer, basic ou custom.`;
				}

				if ("secretName" in authProvider && typeof authProvider.secretName !== "string") {
					return `Servidor "${serverName}": "authProvider.secretName" deve ser string.`;
				}
			}
		}
	}

	return null;
}

function parseAndValidateMcpConfig(raw: string): { value?: McpConfig; error?: string } {
	if (!raw.trim() || raw.trim() === "{}") {
		return { value: undefined };
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return { error: "MCP Server Config contém JSON inválido." };
	}

	if (!isRecord(parsed)) {
		return { error: "MCP Server Config deve ser um objeto JSON." };
	}

	if (!("mcpServers" in parsed) || !isRecord(parsed.mcpServers)) {
		return { error: 'MCP Server Config deve conter a chave "mcpServers" como objeto.' };
	}

	for (const [serverName, config] of Object.entries(parsed.mcpServers)) {
		const error = validateServerConfig(config, serverName);
		if (error) {
			return { error };
		}
	}

	const typedConfig: McpConfig = {
		mcpServers: parsed.mcpServers as McpConfig["mcpServers"],
	};

	return { value: typedConfig };
}

// --- Component ---

export const McpFormPage: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { mcpId } = useParams<{ mcpId: string }>();

	const isEditing = !!mcpId;
	const stateMcp = (location.state as { mcp?: PublicMcpDTO })?.mcp ?? null;

	const [editingMcp, setEditingMcp] = useState<PublicMcpDTO | null>(stateMcp);
	const [loadingMcp, setLoadingMcp] = useState(isEditing && !stateMcp);
	const [form, setForm] = useState<McpFormState>(stateMcp ? mcpToForm(stateMcp) : emptyForm);
	const [saving, setSaving] = useState(false);
	const time = useRef<NodeJS.Timeout>(undefined);

	useEffect(() => {
		if (!isEditing || stateMcp) return;

		setLoadingMcp(true);
		clearTimeout(time.current);

		time.current = setTimeout(async () => {
			try {
				const mcps = await McpLibraryServices.listMcps();
				const found = mcps.find((m) => String(m.id) === mcpId);
				if (found) {
					setEditingMcp(found);
					setForm(mcpToForm(found));
				} else {
					NotistackServices.showErrorMessage("MCP não encontrado.");
					navigate("/backoffice/mcp-library", { replace: true });
				}
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar MCP.");
				navigate("/backoffice/mcp-library", { replace: true });
			} finally {
				setLoadingMcp(false);
			}
		}, 300);

		return () => clearTimeout(time.current);
	}, [isEditing, mcpId, stateMcp, navigate]);

	const configValidation = useMemo(() => parseAndValidateMcpConfig(form.mcpServerConfig), [form.mcpServerConfig]);
	const toolEnvValidation = useMemo(() => validateToolEnvFields(form.toolEnvFields), [form.toolEnvFields]);
	const slugValidation = useMemo(() => {
		if (isEditing) return null;

		const slug = form.slug.trim();
		if (!slug) {
			return "Slug é obrigatório para novos MCPs. Use o formato 'mcp-([a-z0-9\\-])' (somente minúsculas, números e hífen, ex: 'mcp-exemplo').";
		}

		if (!MCP_SLUG_REGEX.test(slug)) {
			return "Slug inválido. Use o formato 'mcp-([a-z0-9\\-])' (somente minúsculas, números e hífen, ex: 'mcp-exemplo').";
		}

		return null;
	}, [form.slug, isEditing]);

	const handleFormChange = (field: keyof McpFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setForm((prev) => ({ ...prev, [field]: event.target.value }));
	};

	const handleToolEnvFieldChange = <K extends keyof ToolENV>(index: number, key: K, value: ToolENV[K]) => {
		setForm((prev) => ({
			...prev,
			toolEnvFields: prev.toolEnvFields.map((field, i) => (i === index ? { ...field, [key]: value } : field)),
		}));
	};

	const handleToolEnvTypeChange = (index: number, mode: "string" | "number" | "boolean" | "json" | "oauth2") => {
		if (mode !== "oauth2") {
			handleToolEnvFieldChange(index, "type", mode);
			return;
		}
		handleToolEnvFieldChange(index, "type", "OAuth2:google");
	};

	const handleOAuthProviderChange = (index: number, provider: string) => {
		const normalizedProvider = provider.trim() || "default";
		handleToolEnvFieldChange(index, "type", `OAuth2:${normalizedProvider}`);
	};

	const handleAddToolEnvField = () => {
		setForm((prev) => ({
			...prev,
			toolEnvFields: [...prev.toolEnvFields, createEmptyToolEnv()],
		}));
	};

	const handleRemoveToolEnvField = (index: number) => {
		setForm((prev) => ({
			...prev,
			toolEnvFields: prev.toolEnvFields.filter((_, i) => i !== index),
		}));
	};

	const handleGoBack = () => {
		navigate("/backoffice/mcp-library");
	};

	const handleSubmit = useCallback(async () => {
		if (!form.name.trim()) {
			NotistackServices.showErrorMessage("Nome de MCP é obrigatório.");
			return;
		}

		if (slugValidation) {
			NotistackServices.showErrorMessage(slugValidation);
			return;
		}

		if (configValidation.error) {
			NotistackServices.showErrorMessage(configValidation.error);
			return;
		}

		if (toolEnvValidation) {
			NotistackServices.showErrorMessage(toolEnvValidation);
			return;
		}

		setSaving(true);
		try {
			if (isEditing && editingMcp) {
				const payload: UpdateMcpPayload = {
					name: form.name,
					category: form.category || "general",
					description: form.description || undefined,
					icon_url: form.iconUrl || undefined,
					pricing_model: buildPricingModel(form),
					mcp_server_config: configValidation.value as Record<string, any> | undefined,
					tool_env_fields: form.toolEnvFields,
				};
				await McpLibraryServices.updateMcp(editingMcp.id, payload);
				NotistackServices.showSuccessMessage("MCP atualizado com sucesso!");
			} else {
				const payload: CreateMcpPayload = {
					slug: form.slug.trim(),
					name: form.name,
					category: form.category || "general",
					description: form.description || undefined,
					icon_url: form.iconUrl || undefined,
					pricing_model: buildPricingModel(form),
					mcp_server_config: configValidation.value as Record<string, any> | undefined,
					tool_env_fields: form.toolEnvFields,
				};
				await McpLibraryServices.createMcp(payload);
				NotistackServices.showSuccessMessage("MCP criado com sucesso!");
			}

			navigate("/backoffice/mcp-library");
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao salvar MCP.");
		} finally {
			setSaving(false);
		}
	}, [form, isEditing, editingMcp, configValidation, toolEnvValidation, slugValidation, navigate]);

	if (loadingMcp) {
		return (
			<div className={styles.container}>
				<div className={styles.loadingState}>
					<CircularProgress
						color="inherit"
						size={24}
					/>
					<span>Carregando MCP...</span>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			{/* Header */}
			<div className={styles.pageHeader}>
				<div className={styles.pageHeaderBack}>
					<IconButton
						size="small"
						onClick={handleGoBack}
					>
						<Icon
							path={mdiArrowLeft}
							size={0.9}
						/>
					</IconButton>
					<div>
						<h2>{isEditing ? "Editar MCP" : "Novo MCP"}</h2>
						<p>{isEditing ? `Editando: ${editingMcp?.name ?? mcpId}` : "Preencha os dados para criar um novo MCP."}</p>
					</div>
				</div>
				<div className={styles.headerActions}>
					<button
						className={`${styles.btn} ${styles.btnSecondary}`}
						onClick={handleGoBack}
						disabled={saving}
					>
						Cancelar
					</button>
					<button
						className={`${styles.btn} ${styles.btnPrimary}`}
						onClick={handleSubmit}
						disabled={saving || !!configValidation.error || !!toolEnvValidation || !!slugValidation}
					>
						{saving ? (
							<CircularProgress
								size={14}
								color="inherit"
							/>
						) : isEditing ? (
							"Salvar Alterações"
						) : (
							"Criar MCP"
						)}
					</button>
				</div>
			</div>

			{/* Form Content */}
			<div className={styles.formContent}>
				<div className={styles.formGrid}>
					<div className={styles.formSection}>
						<h4>Informações Básicas</h4>

						<TextField
							label="Slug (identificador único)"
							value={form.slug}
							onChange={handleFormChange("slug")}
							disabled={isEditing}
							error={!isEditing && !!slugValidation}
							fullWidth
							size="small"
							required
							helperText={isEditing ? "O slug não pode ser alterado" : slugValidation || "Ex: mcp-search, mcp-image"}
						/>

						<TextField
							label="Nome"
							value={form.name}
							onChange={handleFormChange("name")}
							fullWidth
							size="small"
							required
						/>

						<TextField
							label="Categoria"
							value={form.category}
							onChange={handleFormChange("category")}
							fullWidth
							size="small"
							helperText="Ex: general, marketing, development"
						/>

						<TextField
							label="Descrição"
							value={form.description}
							onChange={handleFormChange("description")}
							fullWidth
							size="small"
							multiline
							rows={8}
						/>

						<TextField
							label="URL do Ícone"
							value={form.iconUrl}
							onChange={handleFormChange("iconUrl")}
							fullWidth
							size="small"
						/>
					</div>

					<div className={styles.formSection}>
						<h4>Configurações</h4>

						<TextField
							label="MCP Server Config (JSON)"
							value={form.mcpServerConfig}
							onChange={handleFormChange("mcpServerConfig")}
							fullWidth
							size="small"
							multiline
							rows={15}
							error={!!configValidation.error}
							helperText={configValidation.error || 'Formato esperado: { "mcpServers": { "server-name": { "command": "node" } } }'}
							InputProps={{
								style: { fontFamily: "'JetBrains Mono', monospace", fontSize: 13 },
							}}
						/>
					</div>

					<div className={styles.formSection}>
						<h4>Campos ENV da Ferramenta</h4>

						{form.toolEnvFields.length === 0 && (
							<p className={styles.envHint}>Nenhum campo ENV configurado. Adicione campos para exigir dados na contratação/edição.</p>
						)}

						{form.toolEnvFields.map((field, index) => {
							const mode = getToolEnvMode(field.type);
							return (
								<div
									key={`${field.envName}-${index}`}
									className={styles.envFieldCard}
								>
									<div className={styles.envFieldHeader}>
										<span>Campo #{index + 1}</span>
										<IconButton
											size="small"
											onClick={() => handleRemoveToolEnvField(index)}
										>
											<Icon
												path={mdiTrashCanOutline}
												size={0.7}
											/>
										</IconButton>
									</div>

									<div className={styles.envFieldGrid}>
										<TextField
											label="ENV Name"
											value={field.envName}
											onChange={(event) => handleToolEnvFieldChange(index, "envName", event.target.value)}
											size="small"
											helperText="Ex: OPENAI_API_KEY"
										/>

										<TextField
											label="Label"
											value={field.label}
											onChange={(event) => handleToolEnvFieldChange(index, "label", event.target.value)}
											size="small"
										/>

										<FormControl size="small">
											<InputLabel id={`env-type-${index}`}>Tipo</InputLabel>
											<Select
												labelId={`env-type-${index}`}
												label="Tipo"
												value={mode}
												onChange={(event) =>
													handleToolEnvTypeChange(
														index,
														event.target.value as "string" | "number" | "boolean" | "json" | "oauth2",
													)
												}
											>
												<MenuItem value="string">string</MenuItem>
												<MenuItem value="number">number</MenuItem>
												<MenuItem value="boolean">boolean</MenuItem>
												<MenuItem value="json">json</MenuItem>
												<MenuItem value="oauth2">OAuth2</MenuItem>
											</Select>
										</FormControl>

										{mode === "oauth2" && (
											<>
												<FormControl size="small">
													<InputLabel id={`oauth2-provider-label-${index}`}>OAuth2 Provider</InputLabel>
													<Select
														labelId={`oauth2-provider-label-${index}`}
														label="OAuth2 Provider"
														value={getOAuthProvider(field.type)}
														onChange={(event) => handleOAuthProviderChange(index, event.target.value as any)}
													>
														<MenuItem value="google">google</MenuItem>
													</Select>
												</FormControl>

												<TextField
													label="Escopos OAuth2 (se aplicável)"
													helperText="Lista de escopos separados por vírgula, ex: email, profile"
													value={getOAuthScopes(field).join(", ")}
													onChange={(event) =>
														handleToolEnvFieldChange(
															index,
															"scopes",
															event.target.value.split(",").map((s) => s.trim()),
														)
													}
													size="small"
													multiline
													rows={2}
												/>
											</>
										)}

										<TextField
											label="Descrição"
											value={field.description}
											onChange={(event) => handleToolEnvFieldChange(index, "description", event.target.value)}
											size="small"
											multiline
											rows={2}
										/>

										<FormControlLabel
											className={styles.envRequiredControl}
											control={
												<Checkbox
													size="small"
													checked={field.required}
													onChange={(event) => handleToolEnvFieldChange(index, "required", event.target.checked)}
												/>
											}
											label="Obrigatório"
										/>
									</div>
								</div>
							);
						})}

						<div className={styles.envActions}>
							<Button
								variant="outlined"
								onClick={handleAddToolEnvField}
								startIcon={
									<Icon
										path={mdiPlus}
										size={0.7}
									/>
								}
							>
								Adicionar Campo ENV
							</Button>
						</div>

						{toolEnvValidation && <p className={styles.envError}>{toolEnvValidation}</p>}
					</div>
				</div>
			</div>
		</div>
	);
};
